import { FORCAS_DIAGNOSTICO } from './index.js';

export type RadarSeries = {
  scores: readonly number[];
  stroke: string;
  fill: string;
  label?: string;
};

const COMPARE_COLORS = ['#ffc800', '#60a5fa', '#f472b6', '#34d399', '#a78bfa'];

function pt(cx: number, cy: number, R: number, n: number, i: number, f: number) {
  const ang = ((Math.PI * 2) / n) * i - Math.PI / 2;
  return { x: cx + Math.cos(ang) * R * f, y: cy + Math.sin(ang) * R * f };
}

/** Sobre fundo claro (o app e o PDF): grade cinza, texto preto. */
const COR = { grade: '#c8c8c8', texto: '#0a0a0a', apoio: '#5c5c5c', contorno: '#ffffff' } as const;

function gridAndAxes(cx: number, cy: number, R: number, n: number): string {
  const grade = COR.grade;
  let h = '';
  for (let g = 1; g <= 5; g++) {
    const pts: string[] = [];
    for (let k = 0; k < n; k++) {
      const p = pt(cx, cy, R, n, k, g / 5);
      pts.push(`${p.x},${p.y}`);
    }
    h += `<polygon points="${pts.join(' ')}" fill="none" stroke="${grade}" stroke-width="1"/>`;
  }
  for (let k = 0; k < n; k++) {
    const ep = pt(cx, cy, R, n, k, 1);
    h += `<line x1="${cx}" y1="${cy}" x2="${ep.x}" y2="${ep.y}" stroke="${grade}" stroke-width="1"/>`;
  }
  return h;
}

function polygonForSeries(
  cx: number,
  cy: number,
  R: number,
  n: number,
  sc: readonly number[],
  stroke: string,
  fill: string,
): string {
  const sp: string[] = [];
  sc.forEach((s, i) => {
    const p = pt(cx, cy, R, n, i, s / 10);
    sp.push(`${p.x},${p.y}`);
  });
  return `<polygon points="${sp.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="2"/>`;
}

/** Port do radar() legado — uma série com labels */
export function buildRadarSvgInner(sc: readonly number[], showLabels = true): string {
  const F = FORCAS_DIAGNOSTICO;
  const cor = COR;
  const cx = 250;
  const cy = 250;
  const R = 140;
  const n = 5;
  let h = gridAndAxes(cx, cy, R, n);
  h += polygonForSeries(cx, cy, R, n, sc, '#ffc800', 'rgba(255,200,0,.1)');
  sc.forEach((s, i) => {
    const p = pt(cx, cy, R, n, i, s / 10);
    h += `<circle cx="${p.x}" cy="${p.y}" r="6" fill="${F[i]!.hex}" stroke="${cor.contorno}" stroke-width="2"/>`;
  });
  if (!showLabels) return h;

  const offsets = [
    { lx: 0, ly: -28, nx: 0, ny: -46, anc: 'middle' },
    { lx: 28, ly: -12, nx: 28, ny: 6, anc: 'start' },
    { lx: 22, ly: 18, nx: 22, ny: 36, anc: 'start' },
    { lx: -22, ly: 18, nx: -22, ny: 36, anc: 'end' },
    { lx: -28, ly: -12, nx: -28, ny: 6, anc: 'end' },
  ];
  F.forEach((f, i) => {
    const base = pt(cx, cy, R, n, i, 1);
    const o = offsets[i]!;
    h += `<text x="${base.x + o.lx}" y="${base.y + o.ly}" text-anchor="${o.anc}" dominant-baseline="middle" font-family="Montserrat,sans-serif" font-size="11" font-weight="800" fill="${f.texto}">${f.icone} ${f.nome}</text>`;
    h += `<text x="${base.x + o.nx}" y="${base.y + o.ny}" text-anchor="${o.anc}" dominant-baseline="middle" font-family="Montserrat,sans-serif" font-size="16" font-weight="900" fill="${f.texto}">${sc[i]!.toFixed(1).replace('.', ',')}</text>`;
  });
  const med = sc.reduce((a, b) => a + b, 0) / 5;
  h += `<text x="${cx}" y="${cy - 12}" text-anchor="middle" dominant-baseline="middle" font-family="Montserrat,sans-serif" font-size="32" font-weight="900" fill="${cor.texto}">${med.toFixed(1).replace('.', ',')}</text>`;
  h += `<text x="${cx}" y="${cy + 12}" text-anchor="middle" dominant-baseline="middle" font-family="Montserrat,sans-serif" font-size="8" font-weight="700" fill="${cor.apoio}" letter-spacing="2">MÉDIA GERAL</text>`;
  return h;
}

export function buildRadarSvgMulti(series: RadarSeries[]): string {
  const cx = 250;
  const cy = 250;
  const R = 140;
  const n = 5;
  let h = gridAndAxes(cx, cy, R, n);
  series.forEach((s) => {
    h += polygonForSeries(cx, cy, R, n, s.scores, s.stroke, s.fill);
  });
  const F = FORCAS_DIAGNOSTICO;
  const offsets = [
    { lx: 0, ly: -28, anc: 'middle' },
    { lx: 28, ly: -12, anc: 'start' },
    { lx: 22, ly: 18, anc: 'start' },
    { lx: -22, ly: 18, anc: 'end' },
    { lx: -28, ly: -12, anc: 'end' },
  ];
  F.forEach((f, i) => {
    const base = pt(cx, cy, R, n, i, 1);
    const o = offsets[i]!;
    h += `<text x="${base.x + o.lx}" y="${base.y + o.ly}" text-anchor="${o.anc}" dominant-baseline="middle" font-family="Montserrat,sans-serif" font-size="11" font-weight="800" fill="${f.texto}">${f.icone} ${f.nome}</text>`;
  });
  return h;
}

export function compareSeriesColor(index: number): { stroke: string; fill: string } {
  const stroke = COMPARE_COLORS[index % COMPARE_COLORS.length]!;
  const fill = stroke.replace('#', '').match(/.{2}/g);
  if (!fill) return { stroke, fill: 'rgba(255,200,0,.12)' };
  const [r, g, b] = fill.map((x) => parseInt(x, 16));
  return { stroke, fill: `rgba(${r},${g},${b},.12)` };
}

type RadarChartProps =
  | { scores: readonly number[]; series?: never }
  | { series: RadarSeries[]; scores?: never };

export function RadarChart(props: RadarChartProps) {
  const inner =
    'series' in props && props.series
      ? buildRadarSvgMulti(props.series)
      : buildRadarSvgInner(props.scores ?? []);

  return (
    <div className="flex flex-col items-center bg-ha-card py-5">
      <svg
        className="max-w-[400px] w-full"
        viewBox="0 0 500 500"
        xmlns="http://www.w3.org/2000/svg"
        dangerouslySetInnerHTML={{ __html: inner }}
      />
      {'series' in props && props.series && props.series.some((s) => s.label) && (
        <ul className="mt-3 flex flex-wrap justify-center gap-3 text-xs">
          {props.series.map((s, i) => (
            <li key={s.label ?? i} className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2" style={{ background: s.stroke }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
